import { Dayjs } from 'dayjs';
import React, { useCallback } from 'react';

import AccountBalanceWalletOutlinedIcon from '@mui/icons-material/AccountBalanceWalletOutlined';
import CloudUploadOutlinedIcon from '@mui/icons-material/CloudUploadOutlined';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import TuneOutlinedIcon from '@mui/icons-material/TuneOutlined';
import { Box, Chip, Divider, Grid, Stack, Tooltip, Typography, useTheme } from '@mui/material';
import { alpha } from '@mui/system';

import SbButton from '@Components/Button';
import DatePicker from '@Components/DatePicker';
import SBDropdown from '@Components/Dropdown';
import SbTextField from '@Components/Textfield';

import { FieldValidationState } from '../../../../hooks/useAiValidation';
import { formatNumber, useLocale } from '../../../../utils/formatNumber';
import {
  InvConfig,
  SelectedParamType,
  billToOptions,
  getAccountGroupKey,
  splitTaxOptions,
} from '../utils/invoiceAccountUtils';

export interface InvoiceConfigurationSectionProps {
  invConfig: InvConfig;
  setInvConfig: React.Dispatch<React.SetStateAction<InvConfig>>;
  orgName: string;
  isWyngsMy: boolean;
  sacCodeOptions: { display: string; value: string }[];
  taxInformationDropdownOptions: { display: string; value: string }[];
  selectedAccountRows: SelectedParamType[];
  totalSelectedShipmentsCount: number;
  totalSelectedAmount: number;
  primaryCurrencySymbol: string;
  loadingD365: boolean;
  onSendTo365: () => void;
  taxnoValidationState?: FieldValidationState;
  onApplyTaxnoSuggestion: (suggestion: string) => void;
}

const InvoiceConfigurationSection = React.memo(function InvoiceConfigurationSection(
  props: InvoiceConfigurationSectionProps,
) {
  const {
    invConfig,
    setInvConfig,
    orgName,
    isWyngsMy,
    sacCodeOptions,
    taxInformationDropdownOptions,
    selectedAccountRows,
    totalSelectedShipmentsCount,
    totalSelectedAmount,
    primaryCurrencySymbol,
    loadingD365,
    onSendTo365,
    taxnoValidationState,
    onApplyTaxnoSuggestion,
  } = props;

  const theme = useTheme();
  const locale = useLocale();

  const handleInvoiceDateChange = useCallback(
    (date: Dayjs | null) => {
      setInvConfig(prev => ({
        ...prev,
        invoiceDate: date,
        dueDate: date ? date.add(1, 'month') : null,
      }));
    },
    [setInvConfig],
  );

  const handleDueDateChange = useCallback(
    (date: Dayjs | null) => {
      setInvConfig(prev => ({ ...prev, dueDate: date }));
    },
    [setInvConfig],
  );

  const handleTaxBreakdownChange = useCallback(
    (value: string[]) => {
      setInvConfig(prev => ({ ...prev, taxBreakdown: value }));
    },
    [setInvConfig],
  );

  const handleSacCodeChange = useCallback(
    (value: string[]) => {
      setInvConfig(prev => ({ ...prev, sacCode: value }));
    },
    [setInvConfig],
  );

  const handleSplitTaxChange = useCallback(
    (value: string[]) => {
      setInvConfig(prev => ({ ...prev, splitTax: value }));
    },
    [setInvConfig],
  );

  const handleBillToChange = useCallback(
    (value: string[]) => {
      setInvConfig(prev => ({ ...prev, billTo: value }));
    },
    [setInvConfig],
  );

  const handleBillNameChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      const { value } = e.target;
      setInvConfig(prev => ({ ...prev, billname: value }));
    },
    [setInvConfig],
  );

  const handleBillAddressChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      const { value } = e.target;
      setInvConfig(prev => ({ ...prev, billaddress: value }));
    },
    [setInvConfig],
  );

  const handleTaxNameChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      const { value } = e.target;
      setInvConfig(prev => ({ ...prev, taxname: value }));
    },
    [setInvConfig],
  );

  const handleTaxNoChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      const { value } = e.target;
      setInvConfig(prev => ({ ...prev, taxno: value }));
    },
    [setInvConfig],
  );

  const isThirdParty = invConfig?.billTo && invConfig.billTo[0] === 'third_party';

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        minHeight: 0,
        borderRadius: '14px',
        border: `1px solid ${theme.palette.divider}`,
        bgcolor: alpha(theme.palette.primary.main, 0.015),
        p: { xs: 1.75, sm: 2.25 },
        overflowY: 'auto',
      }}
    >
      {/* Configuration Header */}
      <Stack direction="row" spacing={1.25} alignItems="center" mb={2} sx={{ flexShrink: 0 }}>
        <Box
          sx={{
            width: 30,
            height: 30,
            borderRadius: '8px',
            bgcolor: alpha(theme.palette.primary.main, 0.1),
            color: 'primary.main',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
          }}
        >
          <TuneOutlinedIcon sx={{ fontSize: 18 }} />
        </Box>
        <Box sx={{ minWidth: 0 }}>
          <Typography sx={{ fontWeight: 700, fontSize: '0.96rem', color: 'text.primary', lineHeight: 1.2 }}>
            Invoice Configuration
          </Typography>
          <Typography sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>
            Set billing dates, tax rules, and recipient details
          </Typography>
        </Box>
      </Stack>

      {/* Form Controls */}
      <Grid container spacing={1.75} sx={{ flexShrink: 0 }}>
        <Grid item xs={12} sm={6}>
          <DatePicker
            label="Invoice Date"
            value={invConfig.invoiceDate}
            handleChange={handleInvoiceDateChange}
            required
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <DatePicker
            label="Due Date"
            value={invConfig.dueDate}
            handleChange={handleDueDateChange}
            minDate={invConfig.invoiceDate ? invConfig.invoiceDate.add(1, 'day') : undefined}
            disabled
            required
          />
        </Grid>

        {orgName !== 'Cargo Solutions Network' && (
          <>
            <Grid item xs={12} sm={6}>
              <SBDropdown
                label="Select Taxation"
                id="modal-taxBreakdown"
                placeholder="Select"
                options={taxInformationDropdownOptions}
                onChange={handleTaxBreakdownChange}
                name="taxBreakdown"
                value={invConfig.taxBreakdown || []}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <SBDropdown
                label="SAC Code"
                id="modal-sacCode"
                placeholder="Select"
                options={sacCodeOptions}
                onChange={handleSacCodeChange}
                name="sacCode"
                value={invConfig.sacCode || []}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <SBDropdown
                label="Tax Breakdown"
                id="modal-splitTax"
                placeholder="Select"
                options={splitTaxOptions}
                onChange={handleSplitTaxChange}
                name="splitTax"
                value={invConfig.splitTax || []}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <SBDropdown
                label="Billing To"
                id="modal-billTo"
                placeholder="Select"
                options={billToOptions}
                onChange={handleBillToChange}
                name="billTo"
                value={invConfig.billTo || []}
              />
            </Grid>
          </>
        )}

        {/* Conditional Third-Party Billing Fields */}
        {isThirdParty && (
          <Grid item xs={12}>
            <Box
              sx={{
                p: 1.5,
                mt: 0.25,
                borderRadius: '10px',
                bgcolor: 'background.paper',
                border: `1px solid ${theme.palette.divider}`,
              }}
            >
              <Typography
                sx={{
                  fontSize: '0.74rem',
                  fontWeight: 700,
                  textTransform: 'uppercase',
                  color: 'text.secondary',
                  mb: 1.25,
                }}
              >
                Third-Party Billing Information
              </Typography>
              <Grid container spacing={1.5}>
                <Grid item xs={12} sm={6}>
                  <SbTextField
                    placeholder="Bill Name"
                    label="Bill Name"
                    name="billname"
                    value={invConfig.billname}
                    onChange={handleBillNameChange}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <SbTextField
                    placeholder="Bill Address"
                    label="Bill Address"
                    name="billaddres"
                    value={invConfig.billaddress}
                    onChange={handleBillAddressChange}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <SbTextField
                    placeholder="Tax Name"
                    label="Tax Name"
                    name="taxname"
                    value={invConfig.taxname}
                    onChange={handleTaxNameChange}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <SbTextField
                    placeholder="Tax No"
                    label="Tax No"
                    name="taxno"
                    value={invConfig.taxno}
                    onChange={handleTaxNoChange}
                    validationState={taxnoValidationState}
                    onApplySuggestion={onApplyTaxnoSuggestion}
                  />
                </Grid>
              </Grid>
            </Box>
          </Grid>
        )}

        {!isWyngsMy && orgName !== 'Cargo Solutions Network' && (
          <Grid item xs={12}>
            <SbButton
              variant="outlined"
              startIcon={<CloudUploadOutlinedIcon fontSize="small" />}
              sx={{
                borderRadius: '8px !important',
                padding: '6px 14px !important',
                fontSize: '0.78rem !important',
                width: '100%',
              }}
              onClick={onSendTo365}
              disabled={
                !(invConfig.invoiceDate && invConfig.dueDate) || selectedAccountRows.length === 0 || loadingD365
              }
              loading={loadingD365}
            >
              Send to D365
            </SbButton>
          </Grid>
        )}
      </Grid>

      <Divider sx={{ my: 2, flexShrink: 0 }} />

      {/* Integrated Preview Invoices & Financial Summary */}
      <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: '200px' }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center" mb={1} sx={{ flexShrink: 0 }}>
          <Stack direction="row" spacing={1} alignItems="center">
            <DescriptionOutlinedIcon sx={{ fontSize: 17, color: 'primary.main' }} />
            <Typography sx={{ fontWeight: 700, fontSize: '0.88rem', color: 'text.primary' }}>
              Preview Invoices
            </Typography>
          </Stack>
          <Chip
            size="small"
            color={selectedAccountRows.length > 0 ? 'primary' : 'default'}
            label={`No. of Invoices : ${selectedAccountRows.length}`}
            sx={{ height: 22, fontSize: '0.72rem', fontWeight: 700 }}
          />
        </Stack>

        {/* Compact Queued Invoice List */}
        <Box
          sx={{
            flex: 1,
            minHeight: '100px',
            maxHeight: '210px',
            overflowY: 'auto',
            borderRadius: '10px',
            bgcolor: 'background.paper',
            border: `1px solid ${theme.palette.divider}`,
            p: selectedAccountRows.length === 0 ? 2 : 1,
          }}
        >
          {selectedAccountRows.length === 0 ? (
            <Stack alignItems="center" justifyContent="center" sx={{ height: '100%', py: 1.5, textAlign: 'center' }}>
              <AccountBalanceWalletOutlinedIcon sx={{ fontSize: 26, color: 'text.disabled', mb: 0.5 }} />
              <Typography sx={{ fontSize: '0.78rem', fontWeight: 600, color: 'text.secondary' }}>
                No accounts selected yet
              </Typography>
              <Typography sx={{ fontSize: '0.72rem', color: 'text.secondary' }}>
                Check accounts or individual shipments on the left to queue invoices.
              </Typography>
            </Stack>
          ) : (
            <Stack spacing={0.75}>
              {selectedAccountRows.map((row, idx) => (
                <Box
                  key={getAccountGroupKey(row) || idx}
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    px: 1.25,
                    py: 0.75,
                    borderRadius: '8px',
                    bgcolor: alpha(theme.palette.primary.main, 0.03),
                    border: `1px solid ${alpha(theme.palette.primary.main, 0.12)}`,
                  }}
                >
                  <Stack direction="row" spacing={1} alignItems="center" sx={{ minWidth: 0, flex: 1, mr: 1 }}>
                    <Typography sx={{ fontSize: '0.74rem', fontWeight: 700, color: 'text.secondary', flexShrink: 0 }}>
                      #{idx + 1}
                    </Typography>
                    <Tooltip title={`Account Number: ${row.accountNumber}`} arrow placement="top">
                      <Typography
                        noWrap
                        sx={{ fontSize: '0.8rem', fontWeight: 700, color: 'text.primary', maxWidth: '120px' }}
                      >
                        {row.accountNumber}
                      </Typography>
                    </Tooltip>
                    {orgName !== 'Cargo Solutions Network' && row.systemName && (
                      <Tooltip title={`System: ${row.systemName}`} arrow placement="top">
                        <Typography noWrap sx={{ fontSize: '0.74rem', color: 'text.secondary', maxWidth: '140px' }}>
                          • {row.systemName}
                        </Typography>
                      </Tooltip>
                    )}
                  </Stack>
                  <Tooltip title={`${row.shipments.length} shipment(s) selected in this invoice`} arrow placement="top">
                    <Chip
                      size="small"
                      label={`${row.shipments.length} ${row.shipments.length === 1 ? 'shipment' : 'shipments'}`}
                      sx={{
                        height: 20,
                        fontSize: '0.7rem',
                        fontWeight: 600,
                        bgcolor: alpha(theme.palette.primary.main, 0.1),
                        color: 'primary.main',
                        flexShrink: 0,
                      }}
                    />
                  </Tooltip>
                </Box>
              ))}
            </Stack>
          )}
        </Box>

        {/* Bottom Summary Callout */}
        <Box
          sx={{
            mt: 1.5,
            mb: 2.5,
            p: 1.25,
            borderRadius: '10px',
            bgcolor: 'background.paper',
            border: `1px solid ${theme.palette.divider}`,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            flexShrink: 0,
          }}
        >
          <Box sx={{ minWidth: 0 }}>
            <Typography sx={{ fontSize: '0.72rem', fontWeight: 600, color: 'text.secondary' }}>
              SELECTED TOTAL ({totalSelectedShipmentsCount} SHIPMENTS)
            </Typography>
            <Tooltip
              title={`Total Selected Amount: ${primaryCurrencySymbol} ${formatNumber(totalSelectedAmount, locale)}`}
              arrow
              placement="top"
            >
              <Typography noWrap sx={{ fontSize: '1.05rem', fontWeight: 800, color: 'primary.main' }}>
                {primaryCurrencySymbol} {formatNumber(totalSelectedAmount, locale)}
              </Typography>
            </Tooltip>
          </Box>
          <Chip
            size="small"
            label={selectedAccountRows.length > 0 ? 'Ready' : 'Awaiting Selection'}
            color={selectedAccountRows.length > 0 ? 'success' : 'default'}
            variant="outlined"
            sx={{ fontWeight: 700, fontSize: '0.7rem', flexShrink: 0 }}
          />
        </Box>
      </Box>
    </Box>
  );
});

export default InvoiceConfigurationSection;
